import React, { useState } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { List } from '@heartinternet/hi-ui';
import { ListItem } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { Textarea } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Modal } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { Toggle } from '@heartinternet/hi-ui';
import './TransferPage.css';

/** Green tick used for the feature list — a plain checkmark (not a circle, which
 *  reads as a selected radio button), matching the ticks on the marketing site. */
const TickIcon = () => (
  <svg
    className="hi-ui-transfer-page__tick"
    width="18"
    height="18"
    viewBox="0 0 20 20"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.8 6.8-6.8a1 1 0 0 1 1.4 0z"
      fill="#4e9758"
    />
  </svg>
);

// ─── Constants ─────────────────────────────────────────────────────────────

const MAX_DOMAIN_COUNT = 25;

// Placeholder shows the expected shape: one domain per line. Final examples TBC.
const DOMAIN_PLACEHOLDER = ['myfirstdomain.com', 'myseconddomain.co.uk', 'mythirddomain.net'].join(
  '\n'
);

const FREE_SERVICES = [
  'Web Forwarding',
  'Email Forwarding',
  'Catch all Email Forwarding',
  'Domain "For Sale" Page',
  'DNS Management',
  'Change Nominet Tag (UK domains)',
  'Update Domain Contacts',
  'Easy-to-use Home Page Creator',
  'And Much More!',
];

/** Curated price-list rows, matching the legacy template's hand-picked TLDs. */
interface PriceRow {
  label: React.ReactNode;
  /** products key to read the price from */
  key: string;
  /** ".co.uk" shows "Free Transfer-In!" when its price is zero */
  free?: boolean;
  prefix?: string;
  note?: React.ReactNode;
}
const PRICE_ROWS: PriceRow[] = [
  { label: '.co.uk/.me.uk/.org.uk', key: '.co.uk', free: true },
  {
    label: (
      <>
        .com/.net/.org
        <br />
        .info/.name/.biz
      </>
    ),
    key: '.com',
    prefix: 'Just ',
    note: (
      <>
        <br />
        We&apos;ll add an extra year to your domain registration for free!
      </>
    ),
  },
  {
    label: (
      <>
        .uk.com/.uk.net
        <br />
        .gb.net/.eu.com
      </>
    ),
    key: '.uk.com',
  },
  { label: '.eu', key: '.eu' },
  { label: '.me', key: '.me' },
  { label: '.tv', key: '.tv' },
  { label: '.cc', key: '.cc' },
  { label: '.xxx', key: '.xxx' },
  { label: '.co', key: '.co' },
  { label: '.mobi', key: '.mobi' },
  { label: '.tel', key: '.tel' },
];

// Support-database articles (the legacy /manage/transfer-{uk,cno,eu}.cgi guides
// no longer exist). The .uk and gTLD topics have direct articles; there's no
// .eu-specific article, so the third link goes to the transfer category index.
const FAQ_LINKS = [
  {
    href: 'https://www.heartinternet.uk/support/article/how-do-i-transfer-my-uk-domain-to-heart-internet.html',
    label: 'How do I transfer my .uk domain to Heart Internet?',
  },
  {
    href: 'https://www.heartinternet.uk/support/article/how-do-i-transfer-a-gtld-com-net-org-biz-etc-domain-to-you.html',
    label: 'How do I transfer a gTLD (.com, .net, .org, .biz, etc) domain to you?',
  },
  {
    href: 'https://www.heartinternet.uk/support/category/domain-names/domain-transfers',
    label: 'Domains support center',
  },
];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TransferResult {
  /** The domain name this outcome relates to */
  domain: string;
  success?: boolean;
  /** A bare .uk domain to upsell after a successful qualified-.uk transfer */
  offerUk?: string;
  /** Domain id — an in-progress transfer can be restarted */
  restartOption?: string;
  /** Domain id — a previously-failed transfer can be restarted */
  restartFailedOption?: string;
  failedDifferentCustomer?: boolean;
  offerHostingTransfer?: boolean;
  pendingTransfer?: boolean;
  badtag?: 'badtag' | 'already-yours' | 'already-exists';
  ipstag?: string;
  nonexistent?: boolean;
  tooNew?: boolean;
  locked?: boolean;
  unavailable?: boolean;
  pendingDeletion?: boolean;
  noSupport?: boolean;
}

export interface TransferPageProps {
  /** Per-domain outcomes from the previous POST (server-rendered) */
  results?: TransferResult[];
  /** TLD → price string (e.g. { '.co.uk': '0', '.com': '9.99' }) */
  products?: Record<string, string>;
  /** Presales www domain, used for the .uk upsell link */
  wwwDomain?: string;
  /** Sticky state for the "leave nameservers unchanged" checkbox */
  noNsChange?: boolean;
  /** Form action URL */
  action?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Call a global defined by the legacy JS still loaded on the page. */
function callMediateTransfer(domain: string, hosting: boolean) {
  const win = window as typeof window & {
    mediate_transfer?: (domain: string, hosting: boolean) => void;
  };
  win.mediate_transfer?.(domain, hosting);
}

/** Explanation shown in the "leave nameservers unchanged?" popup — copied
 *  verbatim from the legacy transfer.js domain_detail_data. */
const NS_CHANGE_DETAIL =
  'Use this option if you are moving your domain name to Heart Internet and are retaining ' +
  'email or domain services elsewhere. Please note that if you select this option, free ' +
  'services such as web and email forwarding will not work. However, you will be able to ' +
  "fully manage the domain's details and you can update the nameservers at any time in the future.";

function isFreePrice(price: string | undefined): boolean {
  return parseFloat(price || '0') === 0;
}

/** Count of non-empty lines a user has entered. */
function countDomains(raw: string): number {
  return raw
    .split(/\r?\n/)
    .map((d) => d.trim())
    .filter(Boolean).length;
}

// ─── Result rendering ──────────────────────────────────────────────────────

/** "in another account" mediate notice — reused for already-exists and
 *  restart-failed-for-a-different-customer. Mirrors the legacy mediate_transfer macro. */
function MediateNotice({
  domain,
  hosting,
}: {
  domain: string;
  hosting?: boolean;
}): React.ReactElement {
  if (hosting) {
    return (
      <Notice variant="error" role="alert">
        <Text variant="body">The domain {domain} is in another Heart Internet Account.</Text>
        <Text variant="body">
          Click{' '}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              callMediateTransfer(domain, true);
            }}
          >
            here
          </Link>{' '}
          to contact the account owner and request that permission be given to us to transfer the
          hosting package along with the domain name. (We may charge up to £50.00+VAT for this
          service).
        </Text>
        <Text variant="body" inline>
          Click{' '}
          <Link
            href="#"
            onClick={(e) => {
              e.preventDefault();
              callMediateTransfer(domain, false);
            }}
          >
            here
          </Link>{' '}
          to contact the account owner and request that only the domain name should be transferred.
        </Text>
      </Notice>
    );
  }
  return (
    <Notice variant="error" role="alert">
      <Text variant="body" inline>
        The domain {domain} is in another Heart Internet Account; click{' '}
        <Link
          href="#"
          onClick={(e) => {
            e.preventDefault();
            callMediateTransfer(domain, false);
          }}
        >
          here
        </Link>{' '}
        to contact account owner.
      </Text>
    </Notice>
  );
}

function ResultNotices({
  result,
  wwwDomain,
}: {
  result: TransferResult;
  wwwDomain: string;
}): React.ReactElement {
  const { domain } = result;
  const notices: React.ReactNode[] = [];

  // Group A — primary outcome (mutually exclusive in the legacy template).
  if (result.success) {
    notices.push(
      <Notice key="success" variant="success" role="alert">
        <Text variant="body" inline>
          Your transfer for {domain} is now in your <Link href="/manage/basket.cgi">basket</Link>,{' '}
          {result.offerUk ? (
            <>
              you can also{' '}
              <Link
                href={`https://${wwwDomain}/domain-names?domain=${encodeURIComponent(
                  result.offerUk
                )};multi=1`}
              >
                buy {result.offerUk}
              </Link>{' '}
              if you want to.
            </>
          ) : (
            <>
              to finalise your purchase please <Link href="/manage/basket.cgi">checkout</Link>.
            </>
          )}
        </Text>
      </Notice>
    );
  } else if (result.restartOption) {
    notices.push(
      <Notice key="restart" variant="error" role="alert">
        <Text variant="body" inline>
          There&apos;s already a transfer in process for {domain}. Click{' '}
          <Link
            href={`/manage/restart_transfer.cgi?domain=${encodeURIComponent(
              result.restartOption
            )};restart=1`}
          >
            here
          </Link>{' '}
          to restart it.
        </Text>
      </Notice>
    );
  } else if (result.restartFailedOption) {
    if (result.failedDifferentCustomer) {
      notices.push(
        <MediateNotice
          key="restart-failed-mediate"
          domain={domain}
          hosting={result.offerHostingTransfer}
        />
      );
    } else {
      notices.push(
        <Notice key="restart-failed" variant="error" role="alert">
          <Text variant="body" inline>
            A transfer has previously failed for {domain}. Click{' '}
            <Link
              href={`/manage/restart_transfer.cgi?domain=${encodeURIComponent(
                result.restartFailedOption
              )};restart=1`}
            >
              here
            </Link>{' '}
            to restart it.
          </Text>
        </Notice>
      );
    }
  } else if (result.pendingTransfer) {
    notices.push(
      <Notice key="pending-transfer" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} is currently pending transfer at the registry. If you wish to transfer
          this domain to Heart Internet, then the current transfer must be cancelled. If you believe
          this is an error, please contact customer services.
        </Text>
      </Notice>
    );
  }

  // Group B — tag status (mutually exclusive).
  if (result.badtag === 'badtag') {
    notices.push(
      <Notice key="badtag" variant="error" role="alert">
        <Text variant="body">
          Error: The <strong>{domain}</strong> domain is not on our tag please change the{' '}
          <strong>IPSTAG</strong> to{' '}
          <strong>&apos;HEARTINTERNET&apos; or &apos;EXTEND&apos;</strong> before trying to transfer
          to our system.
        </Text>
        <Text variant="body" inline>
          The domain {domain} is currently on {result.ipstag}
        </Text>
      </Notice>
    );
  } else if (result.badtag === 'already-yours') {
    notices.push(
      <Notice key="already-yours" variant="error" role="alert">
        <Text variant="body" inline>
          The {domain} domain is already in our system under your login, and is not in a
          transferrable state. If you believe this is incorrect, please contact support.
        </Text>
      </Notice>
    );
  } else if (result.badtag === 'already-exists') {
    notices.push(
      <MediateNotice key="already-exists" domain={domain} hosting={result.offerHostingTransfer} />
    );
  }

  // Group C — independent flags (can stack).
  if (result.nonexistent) {
    notices.push(
      <Notice key="nonexistent" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} has not been registered. You can{' '}
          <Link href="/manage/domain.cgi">register this domain</Link> through Heart Internet.
        </Text>
      </Notice>
    );
  }
  if (result.tooNew) {
    notices.push(
      <Notice key="too-new" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} has been registered or transferred within the last 60 days and so
          cannot be transferred at this time. Please try again later.
        </Text>
      </Notice>
    );
  }
  if (result.locked) {
    notices.push(
      <Notice key="locked" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} is locked at the registry. This domain must be unlocked before it can
          be transferred.
        </Text>
      </Notice>
    );
  }
  if (result.unavailable && result.badtag !== 'already-exists') {
    notices.push(
      <Notice key="unavailable" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} is not currently available for transfer. If you believe this is an
          error, please contact customer services.
        </Text>
      </Notice>
    );
  }
  if (result.pendingDeletion) {
    notices.push(
      <Notice key="pending-deletion" variant="error" role="alert">
        <Text variant="body" inline>
          The domain {domain} is pending deletion at the registry and so cannot be transferred.
        </Text>
      </Notice>
    );
  }
  if (result.noSupport) {
    notices.push(
      <Notice key="no-support" variant="error" role="alert">
        <Text variant="body" inline>
          We don&apos;t currently support the domain extension {domain}.
        </Text>
      </Notice>
    );
  }

  return <>{notices}</>;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const TransferPage: React.FC<TransferPageProps> = ({
  results = [],
  products = {},
  wwwDomain = 'www.heartinternet.uk',
  noNsChange = false,
  action = '/manage/transfer.cgi',
}) => {
  const [showAllTlds, setShowAllTlds] = useState(false);
  const [showNsInfo, setShowNsInfo] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const allTlds = Object.keys(products).sort();

  const handleSubmit = (formData: FormData, event: React.FormEvent<HTMLFormElement>) => {
    const raw = String(formData.get('domain') || '');
    const count = countDomains(raw);
    if (count === 0) {
      setDomainError('Please enter at least one domain name.');
      return;
    }
    if (count > MAX_DOMAIN_COUNT) {
      setDomainError(`You can transfer a maximum of ${MAX_DOMAIN_COUNT} domains at a time.`);
      return;
    }
    setDomainError(null);
    // Form preventDefaults when onSubmit is set; submit the native form so the
    // existing CGI handles the POST. The native submit does not refire onSubmit.
    // Call via the prototype: the form has an <input name="submit">, which
    // shadows the form's own submit() method on the element.
    HTMLFormElement.prototype.submit.call(event.currentTarget);
  };

  return (
    <div className="hi-ui-transfer-page">
      <div className="hi-ui-transfer-page__content">
        <Text variant="heading-1">Transfer a Domain to Heart Internet</Text>

        {/* Per-domain outcomes from the previous submission */}
        {results.length > 0 && (
          <div className="hi-ui-transfer-page__results">
            {results.map((result, i) => (
              <ResultNotices key={`${result.domain}-${i}`} result={result} wwwDomain={wwwDomain} />
            ))}
          </div>
        )}

        {/* Transfer form */}
        <section className="hi-ui-transfer-page__section">
          <div className="hi-ui-transfer-page__intro">
            <Text variant="body">
              To transfer a domain to Heart Internet, please enter the domain name(s) below.
            </Text>
            <Text variant="body">
              For Nominet domains (ending in .uk) please ensure your current provider has changed
              the tag on your domain name to &apos;HEARTINTERNET&apos; or &apos;EXTEND&apos; before
              starting the process below.
            </Text>
            <Text variant="body">
              Please note that .com, .net, .org, .info, .name and .biz domain names cannot be
              transferred within 60 days of registration or 60 days of expiry.
            </Text>
          </div>

          <Form action={action} method="post" onSubmit={handleSubmit} noValidate>
            <input type="hidden" name="submit" value="TRANSDOM" />
            <Card variant="surface">
              <FormField
                label={
                  <>
                    List domains to transfer{' '}
                    <span className="hi-ui-transfer-page__label-note">
                      (one per line, up to 25 at a time)
                    </span>
                  </>
                }
                error={domainError || undefined}
                fullWidth
                className="hi-ui-transfer-page__domains-field"
              >
                <Textarea
                  name="domain"
                  rows={4}
                  fullWidth
                  placeholder={DOMAIN_PLACEHOLDER}
                  state={domainError ? 'error' : 'default'}
                  onChange={() => domainError && setDomainError(null)}
                />
              </FormField>

              <div className="hi-ui-transfer-page__ns-change">
                <Checkbox name="no_ns_change" defaultChecked={noNsChange}>
                  Leave nameservers unchanged
                </Checkbox>
                <Link
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowNsInfo(true);
                  }}
                >
                  (what is this?)
                </Link>
              </div>

              <div className="hi-ui-transfer-page__submit">
                <Button type="submit" variant="primary">
                  Transfer now
                </Button>
              </div>
            </Card>
          </Form>
        </section>

        {/* Free services */}
        <section className="hi-ui-transfer-page__section">
          <Text variant="heading-2">Why transfer your domain to us?</Text>
          <Text variant="body">
            Transferring your domain to Heart Internet allows you to take advantage of our following
            features at no extra cost:
          </Text>
          <ul className="hi-ui-transfer-page__features">
            {FREE_SERVICES.map((service) => (
              <li key={service} className="hi-ui-transfer-page__feature">
                <TickIcon />
                <Text variant="body" inline>
                  {service}
                </Text>
              </li>
            ))}
          </ul>
        </section>

        {/* Price list */}
        <section className="hi-ui-transfer-page__section">
          <div className="hi-ui-transfer-page__price-header">
            <Text variant="heading-2" id="price-list">
              Domain transfer price list
            </Text>
            <div className="hi-ui-transfer-page__price-toggle">
              <span className="hi-ui-transfer-page__toggle-text">Popular</span>
              <Toggle
                aria-label="All TLDs"
                checked={showAllTlds}
                onChange={(e) => setShowAllTlds(e.target.checked)}
              />
              <span className="hi-ui-transfer-page__toggle-text">All TLDs</span>
            </div>
          </div>

          <Card variant="surface" padding="none">
            <table className="hi-ui-transfer-page__price-table">
              <thead>
                <tr>
                  <th>TLD</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {showAllTlds
                  ? allTlds.map((tld) => (
                      <tr key={tld} data-testid="all-tlds-row">
                        <td>{tld}</td>
                        <td>£{products[tld]}</td>
                      </tr>
                    ))
                  : PRICE_ROWS.map((row) => {
                      const price = products[row.key];
                      const isFree = row.free && isFreePrice(price);
                      // Skip a curated TLD the backend didn't price — avoids
                      // rendering "£undefined" if a key is ever missing.
                      if (!isFree && price === undefined) return null;
                      return (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td>
                            {isFree ? (
                              'Free'
                            ) : (
                              <>
                                {row.prefix}£{price}
                                {row.note}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </Card>
        </section>

        {/* FAQ */}
        <section className="hi-ui-transfer-page__section">
          <Text variant="heading-2" id="domain-xfer-faq">
            Helpful resources
          </Text>
          <List listStyle="disc">
            {FAQ_LINKS.map((faq) => (
              <ListItem key={faq.href}>
                <Link href={faq.href} external>
                  {faq.label}
                </Link>
              </ListItem>
            ))}
          </List>
        </section>
      </div>

      <Modal
        isOpen={showNsInfo}
        onClose={() => setShowNsInfo(false)}
        title="Leave nameservers unchanged"
      >
        <Text variant="body">{NS_CHANGE_DETAIL}</Text>
      </Modal>
    </div>
  );
};
